import {
  Box,
  Divider,
  Flex,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Email } from "features/Resources/Email/models";
import { useForm } from "react-hook-form";
import moment from "moment-timezone";
import CDS from "design-system";
import { setNewEmailInQueryClient, useUpdateEmail } from "features/Resources/Email/hooks";
import EditableScheduledFieldTag, { buildTimezoneDescription } from "features/Resources/Shared/Components/EditableScheduledFieldTag";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import EmailRecipientsField from "features/Resources/Email/Components/Fields/EmailRecipientsField";
import { useEffect, useState } from "react";
import useAutoSave from "components/FormElements/useAutoSave";
import { useQueryClient } from "@tanstack/react-query";
import { apiEmailById } from "helpers/url/api/v2";
import { COMMS_NO_ACTION_MENU_STATUSES, COMMS_NON_EDITABLE_STATUSES } from "features/Resources/Shared/constants";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";
import { removeLocalData } from "features/Resources/Shared/helpers";
import {
  ListIssuesByAction,
  ListIssuesByActionDuringPending,
} from "features/Issues/Components/ListIssues/ListIssuesByAction";
import ActionHeader from "features/Resources/Shared/Components/ActionHeader";
import BccOrCCButton from "features/Resources/Email/Components/BccOrCCButton";
import TrixEditor from "components/FormElements/Fields/TrixTextAreaField/TrixEditor";
import ComposeBox, { useComposeBoxColors } from "features/Resources/Shared/Components/ComposeBox";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";
import { useCurrentCompanyAdminQuery } from "features/Company/hooks";

import { titleize } from "helpers/string";
import { useTaskDetailQuery } from "features/Resources/Task/hooks";

// Strip Trix's default border + horizontal padding so it sits flush inside the compose box.
const BORDERLESS_TRIX_SX = {
  ".trix-container trix-editor": {
    border: "none !important",
    borderRadius: "0 !important",
    padding: "0 !important",
    minHeight: "120px",
  },
  ".trix-container": {
    gap: "8px !important",
  },
  ".trix-button--icon-attach, .trix-button--icon-decrease-nesting-level, .trix-button--icon-increase-nesting-level, .trix-button--icon-undo, .trix-button--icon-redo": {
    display: "none !important",
  },
};

const EMAIL_STATUS_COLORS: Record<string, string> = {
  pending: "blue.400",
  delivered: "green.500",
  failed: "red.500",
  skipped: "gray.400",
  removed: "gray.400",
};

const EMAIL_STATUS_LABELS: Record<string, string> = {
  pending: "Unsent",
  delivered: "Delivered",
  failed: "Failed",
  skipped: "Skipped",
  removed: "Removed",
};

function SendAtFieldTag({ email }: { email: Email }) {
  const { data: dependencyTask } = useTaskDetailQuery(email.deliverAfterDependencyId, {
    enabled: !!email.deliverAfterDependencyId,
  });
  const { data: journey } = useNewHireJourneyDetailQuery(email.newHireJourneyId, {
    enabled: !!email.newHireJourneyId,
  });

  const { mutate: updateEmail, isPending: isSaving } = useUpdateEmail(email.id);
  const isEditable = !COMMS_NON_EDITABLE_STATUSES.includes(email.status || "");

  // Prefer email TZ (the field the backend uses to interpret deliver_at),
  // fall back to the new hire's TZ, then the browser TZ.
  const newHireTz = journey?.newHire?.timezone;
  const emailTz = email.timezone;
  const timezone = emailTz || newHireTz || moment.tz.guess();
  const timezoneDescription = buildTimezoneDescription({ timezone, resourceTz: emailTz, newHireTz });

  const handleSave = (iso: string) => {
    updateEmail({ deliverAt: iso } as Partial<Email> as Email);
  };

  // Once delivered, show the actual send time under a past-tense label.
  if (email.status === "delivered" && email.effectiveDeliverAt) {
    return (
      <CDS.FieldTag label="Sent">
        {moment.tz(email.effectiveDeliverAt, timezone).format("MMM D, YYYY [at] h:mm A")}
      </CDS.FieldTag>
    );
  }

  // Dependency-based scheduling stays read-only — editing means switching schedule mode.
  if (email.deliverAfterDependencyId) {
    const depName = dependencyTask?.name ?? "Dependency";
    return <CDS.FieldTag label="Scheduled">{`After ${depName}`}</CDS.FieldTag>;
  }

  const concreteIso = email.deliverAt
    || (email.deliverImmediately ? email.effectiveDeliverAt || journey?.pendingUpdatedAt : undefined);

  if (concreteIso) {
    return (
      <EditableScheduledFieldTag
        currentIso={concreteIso}
        timezone={timezone}
        timezoneDescription={timezoneDescription}
        displayText={moment.tz(concreteIso, timezone).format("MMM D, YYYY [at] h:mm A")}
        onSave={handleSave}
        isEditable={isEditable}
        isSaving={isSaving}
      />
    );
  }

  if (email.deliverImmediately) {
    return (
      <EditableScheduledFieldTag
        timezone={timezone}
        timezoneDescription={timezoneDescription}
        displayText="Immediately"
        onSave={handleSave}
        isEditable={isEditable}
        isSaving={isSaving}
      />
    );
  }
  return null;
}

function TriggerFieldTag({ email }: { email: Email }) {
  if (!email.deliverAtDescriptor) return null;
  const label = email.forcedById ? "Manually Scheduled" : email.deliverAtDescriptor;
  return <CDS.FieldTag label="Send Logic">{label}</CDS.FieldTag>;
}

interface EmailFormProps {
  data: Email;
  includeHeader: boolean | undefined;
  includeMenu: boolean | undefined;
  includeTemplate?: boolean;
  isPending: boolean;
  includeStatus: boolean;
  includeJourney?: boolean;
}

export default function EmailForm({
  data,
  includeHeader = false,
  includeMenu = false,
  includeTemplate = false,
  isPending,
  includeJourney = true,
}: EmailFormProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useForm<Email>({ defaultValues: data });
  const { watch, control } = form;
  const isDisabled = COMMS_NON_EDITABLE_STATUSES.includes(data.status || "");
  const showActionMenu = !COMMS_NO_ACTION_MENU_STATUSES.includes(data.status || "");
  const { bodyBg: composeBodyBg, bodyHoverBg: composeBodyHoverBg } = useComposeBoxColors();
  const { data: company } = useCurrentCompanyAdminQuery();

  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  const hasCC = (watch("cc") || []).length > 0;
  const hasBCC = (watch("bcc") || []).length > 0;

  useEffect(() => {
    if (hasBCC && !showBCC) setShowBCC(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBCC]);

  useEffect(() => {
    if (hasCC && !showCC) setShowCC(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCC]);

  const transformData = (updateData: Partial<Email>) => ({ email: removeLocalData(updateData) });

  useAutoSave({
    form,
    transformData,
    submitUrl: apiEmailById(data?.id),
    onSuccess: (res: any) => {
      setNewEmailInQueryClient(queryClient, res);
      toast({ title: "Email updated.", status: "success" });
    },
  });

  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" mb={2}>
          <ActionHeader form={form} action={data} isDisabled={isDisabled} />
          {data && includeMenu && showActionMenu && (
            <ResourceOptionMenu prefixId={data.prefixId} includeTemplate={includeTemplate} />
          )}
        </HStack>
      )}

      {/* Pill row */}
      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {/* Status */}
        <CDS.BasePill cursor="default" _hover={{}} flexShrink={0}>
          <Flex direction="column" gap="0">
            <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Status
            </Text>
            <HStack spacing="1" align="center">
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={EMAIL_STATUS_COLORS[data.status] ?? "gray.400"}
                flexShrink={0}
              />
              <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3" whiteSpace="nowrap">
                {EMAIL_STATUS_LABELS[data.status ?? ""] ?? titleize(data.status ?? "")}
              </Text>
            </HStack>
          </Flex>
        </CDS.BasePill>

        {/* Scheduled */}
        <Box flexShrink={0}>
          <SendAtFieldTag email={data} />
        </Box>
        <Box flexShrink={0}>
          <TriggerFieldTag email={data} />
        </Box>

        {/* Onboarding Journey — last in the row */}
        {includeJourney && data.newHireJourneyId && (
          <NewHireStackedLabel journeyId={data.newHireJourneyId} emphasized />
        )}
      </Flex>


      {isPending ? (
        <ListIssuesByActionDuringPending prefixId={data?.prefixId} align="flex-start" />
      ) : (
        <ListIssuesByAction prefixId={data?.prefixId} align="flex-start" />
      )}

      <Divider my="4" />

      <ComposeBox>
        {company && (
          <ComposeBox.HeaderRow label="From">
            <Text fontSize="sm" color="content.primary" noOfLines={1}>
              {company.customEmailDefaultFrom || `${company.name} Onboarding (via Camino) <onboarding@joincamino.com>`}
            </Text>
          </ComposeBox.HeaderRow>
        )}
        {company?.customEmailDefaultReplyTo && (
          <ComposeBox.HeaderRow label="Reply To">
            <Text fontSize="sm" color="content.primary" noOfLines={1}>
              {company.customEmailDefaultReplyTo}
            </Text>
          </ComposeBox.HeaderRow>
        )}
        <ComposeBox.HeaderRow label="To">
          <EmailRecipientsField
            variant="inline"
            label="To"
            fieldName="to"
            form={form}
            disabled={isDisabled}
            includeRelationTarget={false}
            additionalButtons={
              <>
                {!hasCC && !showCC && <BccOrCCButton onClick={() => setShowCC(true)}>CC</BccOrCCButton>}
                {!hasBCC && !showBCC && <BccOrCCButton onClick={() => setShowBCC(true)}>BCC</BccOrCCButton>}
              </>
            }
          />
        </ComposeBox.HeaderRow>
        {showCC && (
          <ComposeBox.HeaderRow label="CC">
            <EmailRecipientsField
              variant="inline"
              label="CC"
              fieldName="cc"
              form={form}
              disabled={isDisabled}
              includeRelationTarget={false}
            />
          </ComposeBox.HeaderRow>
        )}
        {showBCC && (
          <ComposeBox.HeaderRow label="BCC">
            <EmailRecipientsField
              variant="inline"
              label="BCC"
              fieldName="bcc"
              form={form}
              disabled={isDisabled}
              includeRelationTarget={false}
            />
          </ComposeBox.HeaderRow>
        )}
        <ComposeBox.HeaderRow bg={composeBodyBg}>
          <Input
            {...form.register("subject")}
            variant="unstyled"
            placeholder="Subject"
            fontSize="md"
            fontWeight="bold"
            color="content.primary"
            isDisabled={isDisabled}
            _placeholder={{ color: "fg.subtle", fontWeight: "normal" }}
          />
        </ComposeBox.HeaderRow>
        <Box
          px={4}
          pt={3}
          pb={2}
          bg={composeBodyBg}
          transition="background-color 0.15s"
          sx={{
            ...BORDERLESS_TRIX_SX,
            "&:hover:not(:focus-within)": { bg: composeBodyHoverBg },
          }}
        >
          <TrixEditor control={control} name="body" alwaysShowToolbar isEditable={!isDisabled} placeholder="Write the body…" emailHeadingSizes />
        </Box>
      </ComposeBox>
    </VStack>
  );
}
